import {
  backupSchema,
  hasPermission,
  rustServerSchema,
  rustWipeResultSchema,
  type Backup,
  type ClientServer,
  type RustServer,
  type WipeKind,
} from '@hearth/shared';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  Archive,
  CheckCircle2,
  Dices,
  Eraser,
  FolderOpen,
  Loader2,
  Power,
  TriangleAlert,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { FormField } from '@/components/form-field';
import { QueryState } from '@/components/query-state';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { apiRequest } from '@/lib/api';
import { errorMessage } from '@/lib/forms';
import { serverQuery } from '@/lib/server-queries';
import { useServerLive } from '@/lib/server-socket';

export const Route = createFileRoute('/_app/server/$server/wipe')({ component: WipeTab });

const rustQuery = (server: string) =>
  queryOptions({
    queryKey: ['server', server, 'rust'],
    queryFn: ({ signal }) =>
      apiRequest(`/api/client/servers/${server}/rust`, { schema: rustServerSchema, signal }),
  });

const KINDS: Record<WipeKind, { title: string; what: string }> = {
  map: {
    title: 'Map wipe',
    what: 'Deletes the map and its saves. Players keep the blueprints they’ve learned.',
  },
  full: {
    title: 'Full wipe',
    what: 'Deletes the map, its saves and every player’s learned blueprints.',
  },
};

function WipeTab() {
  const { server: id } = Route.useParams();
  const { data: server } = useSuspenseQuery(serverQuery(id));
  const rust = useQuery(rustQuery(id));
  const { state } = useServerLive(id);
  const [wiping, setWiping] = useState<WipeKind | null>(null);
  const can = (p: Parameters<typeof hasPermission>[1]) => hasPermission(server.permissions, p);
  return (
    <QueryState query={rust}>
      {(data) => (
        <div className="grid gap-4">
          <SaveFolder server={id} data={data} canBrowse={can('file.read')} />
          {data.wipeError && (
            <Alert variant="destructive">
              <TriangleAlert />
              <AlertTitle>Can’t wipe this server</AlertTitle>
              <AlertDescription>{data.wipeError}</AlertDescription>
            </Alert>
          )}
          {data.wipe && state !== null && state !== 'offline' && (
            <Alert>
              <Power />
              <AlertTitle>Stop the server to wipe it</AlertTitle>
              <AlertDescription>Rust keeps its saves open while it runs.</AlertDescription>
            </Alert>
          )}
          {data.wipe && (
            <div className="grid gap-4 md:grid-cols-2">
              {(['map', 'full'] as const).map((kind) => (
                <Card key={kind} className="gap-3 px-4 py-4">
                  <div>
                    <h2 className="font-medium">{KINDS[kind].title}</h2>
                    <p className="text-muted-foreground text-sm">{KINDS[kind].what}</p>
                  </div>
                  <FileList files={data.wipe![kind]} />
                  {can('file.delete') && (
                    <Button
                      variant="destructive"
                      className="w-fit"
                      disabled={state !== 'offline'}
                      onClick={() => setWiping(kind)}
                    >
                      <Eraser /> {KINDS[kind].title}…
                    </Button>
                  )}
                </Card>
              ))}
            </div>
          )}
          {wiping && (
            <WipeDialog server={server} data={data} kind={wiping} onClose={() => setWiping(null)} />
          )}
        </div>
      )}
    </QueryState>
  );
}

function SaveFolder({
  server,
  data,
  canBrowse,
}: {
  server: string;
  data: RustServer;
  canBrowse: boolean;
}) {
  if (!data.saveFolder) return null;
  return (
    <Card className="flex-row flex-wrap items-center justify-between gap-3 px-4 py-3">
      <div className="min-w-0">
        <p className="text-muted-foreground text-xs">Saves</p>
        <p className="truncate font-mono text-sm">{data.saveFolder}</p>
        {!data.identityFromStartup && (
          <p className="text-muted-foreground text-xs">
            The startup command doesn’t set +server.identity, so this is Rust’s default.
          </p>
        )}
      </div>
      {canBrowse && (
        <Button asChild variant="outline">
          <Link to="/server/$server/files" params={{ server }} search={{ dir: data.saveFolder }}>
            <FolderOpen /> Open folder
          </Link>
        </Button>
      )}
    </Card>
  );
}

function FileList({ files }: { files: string[] }) {
  if (files.length === 0)
    return <p className="text-muted-foreground text-sm">Nothing to delete: no saves yet.</p>;
  return (
    <details className="text-sm">
      <summary className="cursor-pointer">
        {files.length} file{files.length === 1 ? '' : 's'} will be deleted
      </summary>
      <ul className="text-muted-foreground mt-2 grid gap-1 font-mono text-xs">
        {files.map((f) => (
          <li key={f} className="truncate">
            {f}
          </li>
        ))}
      </ul>
    </details>
  );
}

const backupList = z.object({ data: z.array(backupSchema) });

/** Offers a backup first, then asks for the server's name before wiping. */
function WipeDialog({
  server,
  data,
  kind,
  onClose,
}: {
  server: ClientServer;
  data: RustServer;
  kind: WipeKind;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const id = server.identifier;
  const can = (p: Parameters<typeof hasPermission>[1]) => hasPermission(server.permissions, p);
  const canBackup = can('backup.create');
  const [backupId, setBackupId] = useState<string | null>(null);
  const [skipped, setSkipped] = useState(!canBackup);
  const [confirm, setConfirm] = useState('');
  const editable = (v: RustServer['world']['seed']) => !!v && v.editable && can('startup.update');
  const [seed, setSeed] = useState(data.world.seed?.value ?? '');
  const [size, setSize] = useState(data.world.size?.value ?? '');

  const backup = useQuery({
    // Not the Backups tab's key: this list is parsed with a smaller schema.
    queryKey: ['server', id, 'backups', 'before-wipe'],
    queryFn: ({ signal }) =>
      apiRequest(`/api/client/servers/${id}/backups`, { schema: backupList, signal }),
    enabled: backupId !== null,
    refetchInterval: (q) =>
      q.state.data?.data.find((b) => b.uuid === backupId)?.isSuccessful === null ? 2000 : false,
    select: (r): Backup | undefined => r.data.find((b) => b.uuid === backupId),
  });
  const startBackup = useMutation({
    mutationFn: () =>
      apiRequest(`/api/client/servers/${id}/backups`, {
        method: 'POST',
        body: { name: `Before ${kind} wipe`, ignored: '' },
        schema: backupSchema,
      }),
    onSuccess: (b) => setBackupId(b.uuid),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const backupDone = backup.data?.isSuccessful === true;
  const backupFailed = backup.data?.isSuccessful === false;

  const wipe = useMutation({
    mutationFn: () =>
      apiRequest(`/api/client/servers/${id}/wipe`, {
        method: 'POST',
        body: {
          kind,
          confirm,
          ...(editable(data.world.seed) && seed !== data.world.seed?.value
            ? { worldSeed: seed }
            : {}),
          ...(editable(data.world.size) && size !== data.world.size?.value
            ? { worldSize: size }
            : {}),
        },
        schema: rustWipeResultSchema,
      }),
    onSuccess: (r) => {
      toast.success(
        `Deleted ${r.deleted.length} file${r.deleted.length === 1 ? '' : 's'}.` +
          (r.changed.length ? ' The new map is used on the next start.' : ''),
      );
      if (!r.daemonSynced) toast.warning(`The node didn’t get the new settings: ${r.daemonError}`);
      void queryClient.invalidateQueries({ queryKey: rustQuery(id).queryKey });
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const ready = (skipped || backupDone) && confirm.trim() === server.name.trim();
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{KINDS[kind].title}</DialogTitle>
          <DialogDescription>{KINDS[kind].what} This can’t be undone.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          {canBackup && (
            <div className="grid gap-2 rounded-md border px-3 py-3 text-sm">
              <p className="font-medium">1. Back up first</p>
              {backupId === null ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={() => startBackup.mutate()}
                    disabled={startBackup.isPending}
                  >
                    {startBackup.isPending ? <Loader2 className="animate-spin" /> : <Archive />}
                    Create a backup
                  </Button>
                  {!skipped && (
                    <Button variant="ghost" onClick={() => setSkipped(true)}>
                      Wipe without a backup
                    </Button>
                  )}
                </div>
              ) : backupDone ? (
                <p className="flex items-center gap-2">
                  <CheckCircle2 className="text-game size-4" /> Backup finished.
                </p>
              ) : backupFailed ? (
                <p className="text-destructive">
                  The backup failed. Check the Backups tab before wiping.
                </p>
              ) : (
                <p className="flex items-center gap-2">
                  <Loader2 className="size-4 animate-spin" /> Backing up…
                </p>
              )}
              {skipped && backupId === null && (
                <p className="text-muted-foreground">Wiping without a backup.</p>
              )}
            </div>
          )}

          {(editable(data.world.seed) || editable(data.world.size)) && (
            <div className="grid gap-3 sm:grid-cols-2">
              {editable(data.world.seed) && (
                <FormField label="Map seed">
                  <div className="flex gap-2">
                    <Input
                      value={seed}
                      onChange={(e) => setSeed(e.target.value.trim())}
                      maxLength={10}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      aria-label="Random seed"
                      onClick={() => setSeed(String(Math.floor(Math.random() * 2_147_483_647)))}
                    >
                      <Dices />
                    </Button>
                  </div>
                </FormField>
              )}
              {editable(data.world.size) && (
                <FormField label="Map size">
                  <Input
                    value={size}
                    onChange={(e) => setSize(e.target.value.trim())}
                    inputMode="numeric"
                    maxLength={5}
                  />
                </FormField>
              )}
            </div>
          )}

          <FormField label={`Type “${server.name}” to confirm`}>
            <Input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="off"
            />
          </FormField>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={!ready || wipe.isPending}
            onClick={() => wipe.mutate()}
          >
            {wipe.isPending && <Loader2 className="animate-spin" />} {KINDS[kind].title}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
